"use client"
import React, { createContext, useContext, useMemo, forwardRef } from "react"
import { tv, VariantProps } from "tailwind-variants"
import { IconProps, Verified } from "../../icons"
import { classNames } from "../../utils/classNames"
import { FlexCenter, FlexCenterProps } from "../FlexCenter"
import { Link } from "../Link"
import { tableRowSizeVariants } from "../Table"
import { Text, TextBodyProps } from "../Text"
import { TextOverflowTooltip } from "../TextOverflowTooltip"
import { Tooltip } from "../Tooltip"

export type CollectionLockupProps = {
  /**
   * Pass in a custom badge component if needed. Otherwise, renders a default badge when badge is present.
   */
  badge?: React.ReactNode

  name: string

  /**
   * Decides whether or not to show the verified badge.
   */
  isVerified?: boolean | null

  /**
   * Decides whether or not to show the text overflow tooltip.
   */
  disableTextOverflowTooltip?: boolean

  /**
   * Supply children when you want to override the default Text behavior
   */
} & FlexCenterProps &
  VariantProps<typeof tableRowSizeVariants>

const CollectionLockupContext = createContext<
  Pick<
    CollectionLockupProps,
    "name" | "isVerified" | "size" | "badge" | "disableTextOverflowTooltip"
  >
>({
  name: "",
  isVerified: false,
  size: "sm",
  badge: undefined,
  disableTextOverflowTooltip: false,
})

const CollectionLockupBadge = forwardRef<
  SVGSVGElement,
  IconProps & {
    innerFill?: IconProps["fill"]
  }
>(function CollectionLockupBadge({ className, size: propsSize, ...rest }, ref) {
  const { size } = useContext(CollectionLockupContext)
  const { verifiedIcon } = tableRowSizeVariants({ size })

  return (
    <Verified
      className={classNames(verifiedIcon(), className)}
      ref={ref}
      size={propsSize}
      {...rest}
    />
  )
})

const CollectionLockupText = forwardRef<
  HTMLDivElement,
  Omit<TextBodyProps, "children"> & {
    children?: (name: string) => React.ReactNode
  }
>(function CollectionLockupText(
  { size: propsSize, className, children, ...rest },
  ref,
) {
  const { name, size, disableTextOverflowTooltip } = useContext(
    CollectionLockupContext,
  )
  const { text } = tableRowSizeVariants({ size: propsSize ?? size })

  return (
    <TextOverflowTooltip
      className="w-auto leading-tight"
      disabled={disableTextOverflowTooltip}
      lines={disableTextOverflowTooltip ? undefined : 1}
      ref={ref}
    >
      <Text.Body
        className={classNames(text(), className)}
        weight="semibold"
        {...rest}
      >
        {children ? children(name) : name}
      </Text.Body>
    </TextOverflowTooltip>
  )
})

const collectionLockupVariants = tv({
  slots: {
    base: "max-w-full",
  },
  variants: {
    size: {
      xs: {
        base: "gap-1",
      },
      sm: {
        base: "gap-1",
      },
      md: {
        base: "gap-1",
      },
      lg: {
        base: "gap-1.5",
      },
      xl: {
        base: "gap-2",
      },
      "2xl": {
        base: "gap-2",
      },
    },
  },
})

const CollectionLockupBase = forwardRef<HTMLDivElement, CollectionLockupProps>(
  function CollectionLockupBase(
    {
      badge,
      children,
      name,
      className,
      isVerified,
      size = "md",
      disableTextOverflowTooltip,
      ...rest
    },
    ref,
  ) {
    const value = useMemo(
      () => ({ name, isVerified, size, badge, disableTextOverflowTooltip }),
      [name, isVerified, size, badge, disableTextOverflowTooltip],
    )
    const { base } = collectionLockupVariants({ size })

    return (
      <CollectionLockupContext.Provider value={value}>
        <FlexCenter
          className={classNames(base(), className)}
          ref={ref}
          {...rest}
        >
          {children ??
            (size === "xl" || size === "lg" ? (
              <Text.Heading asChild size={size === "xl" ? "lg" : "sm"}>
                <CollectionLockup.Text />
              </Text.Heading>
            ) : (
              <CollectionLockupText />
            ))}

          {isVerified &&
            (badge ? (
              badge
            ) : (
              <Tooltip
                content={
                  <div className="max-w-[400px]">
                    This collection belongs to a verified account and has
                    significant interest or sales. To learn about our badging
                    policy,{" "}
                    <Link
                      href="https://support.opensea.io/articles/8867072-what-is-a-verified-account-or-badged-collection"
                      onClick={e => {
                        e.stopPropagation()
                      }}
                    >
                      visit our Help Center
                    </Link>
                    .
                  </div>
                }
              >
                <CollectionLockupBadge />
              </Tooltip>
            ))}
        </FlexCenter>
      </CollectionLockupContext.Provider>
    )
  },
)

export const CollectionLockup = Object.assign(CollectionLockupBase, {
  Badge: CollectionLockupBadge,
  Text: CollectionLockupText,
})
